import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, 
  Plus, 
  Minus,
  Trash2,
  User,
  ShoppingCart,
  CreditCard,
  Banknote,
  Building2,
  FileText,
  Check,
  X,
  AlertTriangle,
  Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewSalePage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const appointmentData = location.state;
  
  // Patient state
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [appointmentId, setAppointmentId] = useState(null);
  
  // Items state
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [activeTab, setActiveTab] = useState('servicios');
  
  // Cart state
  const [cartItems, setCartItems] = useState([]);
  const [descuento, setDescuento] = useState(0);
  const [descuentoPorcentaje, setDescuentoPorcentaje] = useState(0);
  const [notas, setNotas] = useState('');
  
  // Payment state
  const [pagos, setPagos] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newPayment, setNewPayment] = useState({ metodo_pago: 'EFECTIVO', monto: 0, referencia: '', banco: '' });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch services
  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sales/catalog?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  }, [token]);

  // Fetch products with stock
  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inventory/products?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.filter(p => p.cantidad_actual > 0));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchServices();
    fetchProducts();
    
    // Pre-fill patient from appointment
    if (appointmentData?.fromAppointment && appointmentData?.patientId) {
      setAppointmentId(appointmentData.appointmentId);
      // Fetch patient details
      axios.get(`${API}/patients/${appointmentData.patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(response => {
        const patient = response.data;
        const nombre = `${patient.primer_nombre || ''} ${patient.segundo_nombre || ''} ${patient.primer_apellido || ''} ${patient.segundo_apellido || ''}`.trim() || patient.nombre || patient.full_name;
        setSelectedPatient({ ...patient, nombre });
      }).catch(err => {
        console.error('Error fetching patient:', err);
        // Fallback to name from appointment
        if (appointmentData.patientName) {
          setSelectedPatient({ id: appointmentData.patientId, nombre: appointmentData.patientName });
        }
      });
    }
  }, [fetchServices, fetchProducts, appointmentData, token]);

  // Search patients
  const searchPatients = async (query) => {
    if (query.length < 2) {
      setPatients([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/patients?search=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data.slice(0, 10));
    } catch (error) {
      console.error('Error searching patients:', error);
    }
  };

  // Add service to cart
  const addService = (service, nivel = 'NORMAL') => {
    const precio = nivel === 'B' ? service.precio_b : nivel === 'C' ? service.precio_c : service.precio_normal;
    
    const existingIndex = cartItems.findIndex(
      item => item.tipo_item === 'SERVICIO' && item.servicio_id === service.id && item.nivel_precio === nivel
    );
    
    if (existingIndex >= 0) {
      const updated = [...cartItems];
      updated[existingIndex].cantidad += 1;
      updated[existingIndex].subtotal = updated[existingIndex].cantidad * updated[existingIndex].precio_unitario;
      setCartItems(updated);
    } else {
      setCartItems([...cartItems, {
        id: `cart-${Date.now()}`,
        tipo_item: 'SERVICIO',
        servicio_id: service.id,
        descripcion: service.nombre,
        nivel_precio: nivel,
        cantidad: 1,
        precio_unitario: precio,
        subtotal: precio
      }]);
    }
  };

  // Add product to cart
  const addProduct = (product) => {
    const precio = product.precio_venta_sugerido || (product.precio_costo_promedio * 1.3);
    
    const existingIndex = cartItems.findIndex(
      item => item.tipo_item === 'PRODUCTO' && item.producto_id === product.id
    );
    
    if (existingIndex >= 0) {
      const updated = [...cartItems];
      if (updated[existingIndex].cantidad < product.cantidad_actual) {
        updated[existingIndex].cantidad += 1;
        updated[existingIndex].subtotal = updated[existingIndex].cantidad * updated[existingIndex].precio_unitario;
        setCartItems(updated);
      }
    } else {
      setCartItems([...cartItems, {
        id: `cart-${Date.now()}`,
        tipo_item: 'PRODUCTO',
        producto_id: product.id,
        descripcion: product.nombre_comercial,
        nivel_precio: null,
        cantidad: 1,
        precio_unitario: precio,
        subtotal: precio,
        stock_disponible: product.cantidad_actual
      }]);
    }
  };

  // Update cart item quantity
  const updateQuantity = (index, delta) => {
    const updated = [...cartItems];
    const item = updated[index];
    const newQty = item.cantidad + delta;
    
    if (newQty <= 0) {
      updated.splice(index, 1);
    } else {
      if (item.tipo_item === 'PRODUCTO' && newQty > item.stock_disponible) {
        return; // Don't exceed stock
      }
      item.cantidad = newQty;
      item.subtotal = newQty * item.precio_unitario;
    }
    setCartItems(updated);
  };

  // Update cart item price
  const updatePrice = (index, precio) => {
    const updated = [...cartItems];
    updated[index].precio_unitario = parseFloat(precio) || 0;
    updated[index].subtotal = updated[index].cantidad * updated[index].precio_unitario;
    setCartItems(updated);
  };

  // Change price level for service
  const changePriceLevel = (index, nivel, service) => {
    const updated = [...cartItems];
    const precio = nivel === 'B' ? service.precio_b : nivel === 'C' ? service.precio_c : service.precio_normal;
    updated[index].nivel_precio = nivel;
    updated[index].precio_unitario = precio;
    updated[index].subtotal = updated[index].cantidad * precio;
    setCartItems(updated);
  };

  // Remove item from cart
  const removeItem = (index) => {
    const updated = [...cartItems];
    updated.splice(index, 1);
    setCartItems(updated);
  };

  // Add payment method
  const addPayment = () => {
    if (newPayment.monto <= 0) return;
    setPagos([...pagos, { ...newPayment, id: `pago-${Date.now()}` }]);
    setNewPayment({ metodo_pago: 'EFECTIVO', monto: 0, referencia: '', banco: '' });
    setShowPaymentModal(false);
  };

  // Remove payment
  const removePayment = (index) => {
    const updated = [...pagos];
    updated.splice(index, 1);
    setPagos(updated);
  };

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + item.subtotal, 0);
  const descuentoMonto = descuentoPorcentaje > 0 ? subtotal * (descuentoPorcentaje / 100) : descuento;
  const total = subtotal - descuentoMonto;
  const totalPagado = pagos.reduce((sum, p) => sum + p.monto, 0);
  const saldoPendiente = total - totalPagado;

  // Submit sale
  const submitSale = async (guardarPendiente = false) => {
    if (!selectedPatient) {
      setError('Selecciona un paciente');
      return;
    }
    if (cartItems.length === 0) {
      setError('Agrega al menos un ítem');
      return;
    }
    if (!guardarPendiente && totalPagado > total + 0.01) {
      setError('El monto pagado excede el total');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        paciente_id: selectedPatient.id,
        lineas: cartItems.map(item => ({
          tipo_item: item.tipo_item,
          servicio_id: item.servicio_id || null,
          producto_id: item.producto_id || null,
          nivel_precio: item.nivel_precio,
          cantidad: item.cantidad,
          precio_unitario: item.precio_unitario
        })),
        pagos: pagos.map(p => ({
          metodo_pago: p.metodo_pago,
          monto: p.monto,
          referencia: p.referencia || null,
          banco: p.banco || null
        })),
        descuento_monto: descuentoPorcentaje > 0 ? 0 : descuento,
        descuento_porcentaje: descuentoPorcentaje,
        notas: notas || null,
        guardar_pendiente: guardarPendiente
      };

      const response = await axios.post(`${API}/sales`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate(`/admin/ventas/${response.data.id}`);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear la venta');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `Q ${amount?.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  // Filter items by search
  const filteredServices = services.filter(s => 
    s.nombre.toLowerCase().includes(itemSearch.toLowerCase()) ||
    s.codigo.toLowerCase().includes(itemSearch.toLowerCase())
  );
  
  const filteredProducts = products.filter(p => 
    p.nombre_comercial.toLowerCase().includes(itemSearch.toLowerCase()) ||
    p.codigo.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const paymentMethodLabels = {
    EFECTIVO: 'Efectivo',
    TARJETA: 'Tarjeta',
    TRANSFERENCIA: 'Transferencia',
    CHEQUE: 'Cheque',
    OTRO: 'Otro'
  };

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4" data-testid="new-sale-page">
      {/* Left Panel - Items */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Patient Selector */}
        <Card className="border-0 shadow-sm mb-4">
          <CardContent className="p-4">
            <Label className="text-sm font-medium text-slate-700">Paciente</Label>
            {selectedPatient ? (
              <div className="flex items-center justify-between mt-2 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0B3B71]/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-[#0B3B71]" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{selectedPatient.nombre}</p>
                    <p className="text-sm text-slate-500">{selectedPatient.telefono || 'Sin teléfono'}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedPatient(null)}
                  className="text-slate-400 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar paciente por nombre o teléfono..."
                  value={patientSearch}
                  onChange={(e) => {
                    setPatientSearch(e.target.value);
                    searchPatients(e.target.value);
                    setShowPatientSearch(true);
                  }}
                  onFocus={() => setShowPatientSearch(true)}
                  className="pl-10"
                  data-testid="patient-search-input"
                />
                {showPatientSearch && patients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {patients.map((patient) => (
                      <div
                        key={patient.id}
                        className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                        onClick={() => {
                          const nombre = `${patient.primer_nombre || ''} ${patient.segundo_nombre || ''} ${patient.primer_apellido || ''} ${patient.segundo_apellido || ''}`.trim() || patient.nombre || patient.full_name;
                          setSelectedPatient({ ...patient, nombre });
                          setPatientSearch('');
                          setShowPatientSearch(false);
                        }}
                      >
                        <p className="font-medium text-slate-800">
                          {`${patient.primer_nombre || ''} ${patient.primer_apellido || ''}`.trim() || patient.nombre || patient.full_name}
                        </p>
                        <p className="text-sm text-slate-500">{patient.telefono || patient.phone || 'Sin teléfono'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Items Tabs */}
        <Card className="border-0 shadow-sm flex-1 flex flex-col min-h-0">
          <CardContent className="p-4 flex-1 flex flex-col min-h-0">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar servicio o producto..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <TabsList className="bg-slate-100 mb-4">
                <TabsTrigger value="servicios" className="flex-1">
                  <FileText className="h-4 w-4 mr-2" />
                  Servicios ({filteredServices.length})
                </TabsTrigger>
                <TabsTrigger value="medicamentos" className="flex-1">
                  <Package className="h-4 w-4 mr-2" />
                  Medicamentos ({filteredProducts.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="servicios" className="flex-1 overflow-y-auto mt-0">
                <div className="grid gap-2">
                  {filteredServices.map((service) => (
                    <div
                      key={service.id}
                      className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-xs font-mono text-slate-400">{service.codigo}</span>
                          <p className="font-medium text-slate-800">{service.nombre}</p>
                          <Badge variant="outline" className="text-xs mt-1">
                            {service.tipo_label}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => addService(service, 'NORMAL')}
                        >
                          Normal: {formatCurrency(service.precio_normal)}
                        </Button>
                        {service.precio_b > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs"
                            onClick={() => addService(service, 'B')}
                          >
                            B: {formatCurrency(service.precio_b)}
                          </Button>
                        )}
                        {service.precio_c > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs"
                            onClick={() => addService(service, 'C')}
                          >
                            C: {formatCurrency(service.precio_c)}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {filteredServices.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p>No hay servicios</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="medicamentos" className="flex-1 overflow-y-auto mt-0">
                <div className="grid gap-2">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => addProduct(product)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs font-mono text-slate-400">{product.codigo}</span>
                          <p className="font-medium text-slate-800">{product.nombre_comercial}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-semibold text-slate-800">
                            {formatCurrency(product.precio_venta_sugerido || (product.precio_costo_promedio * 1.3))}
                          </p>
                          <p className="text-xs text-slate-500">
                            Stock: {product.cantidad_actual}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredProducts.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p>No hay productos con stock</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Right Panel - Cart */}
      <div className="w-[400px] flex flex-col">
        <Card className="border-0 shadow-sm flex-1 flex flex-col">
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-lg font-semibold text-[#0B3B71] flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Carrito ({cartItems.length})
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 flex-1 flex flex-col min-h-0">
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto mb-4 space-y-2">
              {cartItems.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Agrega ítems al carrito</p>
                </div>
              ) : (
                cartItems.map((item, index) => (
                  <div key={item.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-sm truncate">{item.descripcion}</p>
                        {item.tipo_item === 'SERVICIO' && item.nivel_precio && (
                          <Badge variant="outline" className="text-xs mt-1">
                            Precio {item.nivel_precio}
                          </Badge>
                        )}
                        {item.tipo_item === 'PRODUCTO' && (
                          <span className="text-xs text-slate-400">Stock: {item.stock_disponible}</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-red-500 h-6 w-6 p-0"
                        onClick={() => removeItem(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => updateQuantity(index, -1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center font-mono text-sm">{item.cantidad}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => updateQuantity(index, 1)}
                          disabled={item.tipo_item === 'PRODUCTO' && item.cantidad >= item.stock_disponible}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={item.precio_unitario}
                          onChange={(e) => updatePrice(index, e.target.value)}
                          className="w-24 h-7 text-right font-mono text-sm"
                        />
                        <span className="font-mono text-sm font-semibold text-slate-800 w-24 text-right">
                          {formatCurrency(item.subtotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Totals */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-mono font-medium">{formatCurrency(subtotal)}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Descuento</span>
                <Input
                  type="number"
                  placeholder="Q"
                  value={descuento || ''}
                  onChange={(e) => {
                    setDescuento(parseFloat(e.target.value) || 0);
                    setDescuentoPorcentaje(0);
                  }}
                  className="w-20 h-8 text-right font-mono text-sm"
                />
                <span className="text-slate-400">ó</span>
                <Input
                  type="number"
                  placeholder="%"
                  value={descuentoPorcentaje || ''}
                  onChange={(e) => {
                    setDescuentoPorcentaje(parseFloat(e.target.value) || 0);
                    setDescuento(0);
                  }}
                  className="w-16 h-8 text-right font-mono text-sm"
                />
                <span className="text-slate-400">%</span>
              </div>

              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span className="text-[#0B3B71]">Total</span>
                <span className="font-mono text-[#0B3B71]">{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Payments */}
            <div className="border-t mt-4 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Forma de Pago</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNewPayment({ ...newPayment, monto: saldoPendiente > 0 ? saldoPendiente : total });
                    setShowPaymentModal(true);
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Agregar
                </Button>
              </div>

              {pagos.length > 0 && (
                <div className="space-y-1 mb-2">
                  {pagos.map((pago, index) => (
                    <div key={pago.id} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                      <span className="text-green-700">{paymentMethodLabels[pago.metodo_pago]}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-green-700">{formatCurrency(pago.monto)}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                          onClick={() => removePayment(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className={`flex justify-between text-sm p-2 rounded ${saldoPendiente > 0.01 ? 'bg-amber-50' : 'bg-green-50'}`}>
                <span className={saldoPendiente > 0.01 ? 'text-amber-700' : 'text-green-700'}>
                  {saldoPendiente > 0.01 ? 'Falta' : 'Pagado'}
                </span>
                <span className={`font-mono font-medium ${saldoPendiente > 0.01 ? 'text-amber-700' : 'text-green-700'}`}>
                  {saldoPendiente > 0.01 ? formatCurrency(saldoPendiente) : formatCurrency(totalPagado)}
                </span>
              </div>

              {saldoPendiente > 0.01 && totalPagado > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  La venta quedará como PARCIAL
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="mt-4">
              <Label className="text-sm text-slate-600">Notas (opcional)</Label>
              <Input
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Observaciones..."
                className="mt-1"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertTriangle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 space-y-2">
              <Button
                className="w-full bg-[#0891B2] hover:bg-[#0e7490] h-12"
                onClick={() => submitSale(false)}
                disabled={loading || cartItems.length === 0 || !selectedPatient}
                data-testid="confirm-sale-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Procesando...
                  </span>
                ) : (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    Confirmar Venta
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => submitSale(true)}
                disabled={loading || cartItems.length === 0 || !selectedPatient}
              >
                Guardar como Pendiente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Agregar Método de Pago</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Método</Label>
              <Select value={newPayment.metodo_pago} onValueChange={(v) => setNewPayment({ ...newPayment, metodo_pago: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="EFECTIVO">
                    <span className="flex items-center gap-2"><Banknote className="h-4 w-4" /> Efectivo</span>
                  </SelectItem>
                  <SelectItem value="TARJETA">
                    <span className="flex items-center gap-2"><CreditCard className="h-4 w-4" /> Tarjeta</span>
                  </SelectItem>
                  <SelectItem value="TRANSFERENCIA">
                    <span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Transferencia</span>
                  </SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                  <SelectItem value="OTRO">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monto (Q)</Label>
              <Input
                type="number"
                value={newPayment.monto}
                onChange={(e) => setNewPayment({ ...newPayment, monto: parseFloat(e.target.value) || 0 })}
                className="mt-1 font-mono"
              />
            </div>
            {newPayment.metodo_pago !== 'EFECTIVO' && (
              <div>
                <Label>Referencia</Label>
                <Input
                  value={newPayment.referencia}
                  onChange={(e) => setNewPayment({ ...newPayment, referencia: e.target.value })}
                  placeholder={newPayment.metodo_pago === 'TARJETA' ? 'Últimos 4 dígitos' : 'Número de referencia'}
                  className="mt-1"
                />
              </div>
            )}
            {(newPayment.metodo_pago === 'TRANSFERENCIA' || newPayment.metodo_pago === 'CHEQUE') && (
              <div>
                <Label>Banco</Label>
                <Input
                  value={newPayment.banco}
                  onChange={(e) => setNewPayment({ ...newPayment, banco: e.target.value })}
                  className="mt-1"
                />
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPaymentModal(false)}>
                Cancelar
              </Button>
              <Button className="flex-1 bg-[#0891B2]" onClick={addPayment}>
                Agregar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewSalePage;
